<script setup lang="ts">
// Muted landing video that downloads only when it approaches the viewport and
// plays only while visible, so a page full of clips stays light on phones.
import { useElementVisibility } from '@vueuse/core'

const props = withDefaults(defineProps<{
  src: string
  poster?: string | null
  /** Play while visible; otherwise only the first frame at `frame` seconds is shown. */
  autoplay?: boolean
  frame?: number
}>(), { poster: null, autoplay: true, frame: 0.5 })

const video = useTemplateRef<HTMLVideoElement>('video')
const near = useElementVisibility(video, { rootMargin: '400px 0px' })
const visible = useElementVisibility(video, { threshold: 0.25 })
const loaded = ref(false)

watch(near, (value) => { if (value) loaded.value = true }, { immediate: true })
watch([visible, loaded], ([isVisible, isLoaded]) => {
  const element = video.value
  if (!element || !isLoaded || !props.autoplay) return
  if (isVisible) element.play().catch(() => {})
  else element.pause()
}, { flush: 'post' })
</script>

<template>
  <video
    ref="video"
    :src="loaded ? (autoplay ? src : `${src}#t=${frame}`) : undefined"
    :poster="poster ?? undefined"
    :preload="loaded ? (autoplay ? 'auto' : 'metadata') : 'none'"
    muted
    loop
    playsinline
    disablepictureinpicture
  />
</template>
